import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import { PAYMENT_METHOD_LABELS, PAYMENT_STATUS_LABELS } from "@/lib/bookingLabels";
import { formatGbpFromPence } from "@/types/database";

export type PaymentRow = {
  bookingId: string;
  reference: string;
  performanceDate: string;
  venue: string;
  feePence: number;
  method: string;
  paymentStatus: string;
  bookingStatus: string;
  paidAt: string | null;
  paymentRecordId: string | null;
};

export type PaymentsSummary = {
  receivedPence: number;
  outstandingPence: number;
  paidThisMonthPence: number;
  paidThisYearPence: number;
  expectedUpcomingPence: number;
};

export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: string; unconfigured?: boolean };

function unconfigured<T>(): ApiResult<T> {
  return {
    ok: false,
    error: "Supabase is not configured.",
    unconfigured: true,
  };
}

export async function loadPaymentRows(): Promise<ApiResult<PaymentRow[]>> {
  if (!supabase || !isSupabaseConfigured) return unconfigured();

  const { data: bookings, error } = await supabase
    .from("bookings")
    .select(
      "id, reference, performance_date, fee_pence, payment_status, booking_status, preferred_payment_method, updated_at, clients ( venue_or_client_name )"
    )
    .order("performance_date", { ascending: false });

  if (error) return { ok: false, error: error.message };

  const { data: payments } = await supabase
    .from("payments")
    .select("id, booking_id, method, status, paid_at, amount_pence")
    .order("created_at", { ascending: false });

  const latestByBooking = new Map<
    string,
    { id: string; method: string; status: string; paid_at: string | null }
  >();
  for (const p of payments ?? []) {
    if (!latestByBooking.has(p.booking_id)) {
      latestByBooking.set(p.booking_id, {
        id: p.id,
        method: p.method,
        status: p.status,
        paid_at: p.paid_at,
      });
    }
  }

  type BookingLite = {
    id: string;
    reference: string;
    performance_date: string;
    fee_pence: number;
    payment_status: string;
    booking_status: string;
    preferred_payment_method: string | null;
    updated_at: string;
    clients: { venue_or_client_name: string } | { venue_or_client_name: string }[] | null;
  };

  const rows: PaymentRow[] = ((bookings ?? []) as BookingLite[]).map((b) => {
    const pay = latestByBooking.get(b.id);
    const method = pay?.method || b.preferred_payment_method || "stripe";
    const client = Array.isArray(b.clients) ? b.clients[0] : b.clients;
    return {
      bookingId: b.id,
      reference: b.reference,
      performanceDate: b.performance_date,
      venue: client?.venue_or_client_name ?? "—",
      feePence: b.fee_pence,
      method,
      paymentStatus: b.payment_status,
      bookingStatus: b.booking_status,
      paidAt: pay?.paid_at ?? (b.payment_status === "paid" ? b.updated_at : null),
      paymentRecordId: pay?.id ?? null,
    };
  });

  return { ok: true, data: rows };
}

export function computePaymentsSummary(rows: PaymentRow[]): PaymentsSummary {
  const today = new Date().toISOString().slice(0, 10);
  const now = new Date();
  const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
  const yearStart = `${now.getFullYear()}-01-01`;

  let receivedPence = 0;
  let outstandingPence = 0;
  let paidThisMonthPence = 0;
  let paidThisYearPence = 0;
  let expectedUpcomingPence = 0;

  for (const r of rows) {
    if (r.bookingStatus === "cancelled") continue;

    if (r.paymentStatus === "paid") {
      receivedPence += r.feePence;
      const paidDay = (r.paidAt || "").slice(0, 10);
      if (paidDay >= monthStart) paidThisMonthPence += r.feePence;
      if (paidDay >= yearStart) paidThisYearPence += r.feePence;
    } else if (r.paymentStatus === "awaiting" || r.paymentStatus === "attention") {
      outstandingPence += r.feePence;
      if (r.performanceDate >= today && r.bookingStatus === "scheduled") {
        expectedUpcomingPence += r.feePence;
      }
    }
  }

  return {
    receivedPence,
    outstandingPence,
    paidThisMonthPence,
    paidThisYearPence,
    expectedUpcomingPence,
  };
}

export function rowsToCsv(rows: PaymentRow[]): string {
  const header = [
    "performance_date",
    "venue",
    "reference",
    "amount_gbp",
    "amount_pence",
    "method",
    "payment_status",
    "booking_status",
    "paid_at",
  ];
  const lines = [header.join(",")];
  for (const r of rows) {
    const cols = [
      r.performanceDate,
      csvEscape(r.venue),
      r.reference,
      formatGbpFromPence(r.feePence).replace("£", ""),
      String(r.feePence),
      r.method,
      r.paymentStatus,
      r.bookingStatus,
      r.paidAt ?? "",
    ];
    lines.push(cols.join(","));
  }
  return lines.join("\n");
}

function csvEscape(s: string): string {
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function methodLabel(method: string): string {
  return (
    PAYMENT_METHOD_LABELS[method as keyof typeof PAYMENT_METHOD_LABELS] || method
  );
}

export function paymentStatusLabel(status: string): string {
  return (
    PAYMENT_STATUS_LABELS[status as keyof typeof PAYMENT_STATUS_LABELS] || status
  );
}
